import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import { L10n } from '@syncfusion/ej2-base'

import {
  DialogSystemGLPure,
  DialogYesNoHLPure,
  SimpleLoading,
  SyncfusionFileManager,
} from '../../pures'
import { LocalStorageService, CategoryService, DigitalVaultDocumentService } from '../../../services'
import {
  convertToStandarDate,
  getNavigatorTimezone,
  parseCustomDate,
  setCreditsToDocuments,
} from '../../../utils'

/**
 * @author Edgar Escobedo
 * @description - Component to manage data fetching and actions for Syncfusion's File Manager implementation.
 * @param {Array<{ id: string, title: string }>} breadcrumbItems - Array to construct the breadcrumbs of the document.
 * @param {Array} companies - Array of companies.
 * @param {string} fileManagerPath - String where the file manager will be initialized.
 * @param {Object} selectedCompany - Object with information about the user's selected company.
 * @param {function} setDocumentId - Function to set the id of the selected document.
 * @param {function} setFileManagerPath - Function to set the file manager path.
 * @param {function} setSelectedCategory - Function to set the current category.
 * @returns {JSX.element}
 */
function SyncfusionFileManagerAction({
  breadcrumbItems,
  companies,
  fileManagerPath,
  selectedCompany,
  setBreadcrumbItems,
  setDocumentId,
  setFileManagerPath,
  setSelectedCategory,
  subcategoryId,
  setSubcategoryId,
}) {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const ENV = process.env

  const [isLoading, setIsLoading] = useState(true)
  const [firstLoad, setFirstLoad] = useState(true)

  const [companyId, setCompanyId] = useState(selectedCompany.company.id)

  const [categoriesData, setCategoriesData] = useState([])
  const [documentsData, setDocumentsData] = useState([])
  const [permission, setPermission] = useState({})

  const url = process.env.REACT_APP_MIFIRMA_API
  const localStorageService = new LocalStorageService()
  const language =
    localStorageService.getLocalStorageItem('preferredLanguage') || 'es'
  const timezone = getNavigatorTimezone()
  const token = localStorageService.getLocalStorageItem('token').idToken
  const categoryService = new CategoryService(url)
  const digitalVaultDocumentService = new DigitalVaultDocumentService(url)

  const [showAddFileBtn, setShowAddFileBtn] = useState(false)
  const [strBtn, setStrBtn] = useState(t('fileManager.toolbar.newCategory'))
  const [fileData, setFileData] = useState([])
  const [pendingSubcategoryId, setPendingSubcategoryId] = useState(null)

  const [showLoadingRequesting, setShowLoadingRequesting] = useState(false)
  const [showCompletedRequesting, setShowCompletedRequesting] = useState(false)
  const [showErrorRequesting, setShowErrorRequesting] = useState(false)
  const [messageDialog, setMessageDialog] = useState('')

  const [showFilesCopied, setShowFilesCopied] = useState(false)
  const [filesSuccess, setFilesSuccess] = useState(0)
  const [filesSuccessNames, setFfilesSuccessNames] = useState([])
  const [filesError, setFilesError] = useState(0)
  const [filesErrorNames, setFilesErrorNames] = useState([])
  const [actionType, setActionType] = useState('')
  const [showAnalyzeBtn, setShowAnalyzeBtn] = useState(true)
  const [showModalEntities, setShowModalEntities] = useState(false)
  const [documentsToOCR, setDocumentsToOCR] = useState([])
  const [totalCredits, setTotalCredits] = useState(0)
  const [documentSelectedId, setDocumentSelectedId] = useState(null)

  const fileObj = useRef(null)
  const pathRef = useRef('')
  const isFile = useRef(false)

  function checkDepth({ str }) {
    if (!str) return 1
    const parts = str.replace(/^[/\\]+|[/\\]+$/g, '').split(/[/\\]+/)
    return parts.filter(Boolean).length + 2
  }

  async function refreshFileManager(refresh = true, id = null) {
    await fetchAndSetCategories({ refresh, setLoading: false })
    const { current } = fileObj

    if (checkDepth({ str: current.originalPath }) >= 4) {
      const idsArr = current.originalPath.split('/')
      const subId = idsArr[idsArr.length - 2]

      if (String(id) !== subId) {
        setPendingSubcategoryId(subId)
      }
    }

    if (current) current.refresh()
  }

  function toggleLoadingRequesting(value) {
    setShowLoadingRequesting(value)
  }

  function toggleCompletedRequesting(message, refresh, id) {
    setShowCompletedRequesting(true)
    setMessageDialog(message)

    setTimeout(() => {
      setShowCompletedRequesting(false)
    }, 4000)

    refreshFileManager(refresh, id)
  }

  function toggleErrorRequesting(message) {
    setShowErrorRequesting(true)
    setMessageDialog(message)

    setTimeout(() => {
      setShowErrorRequesting(false)
    }, 4000)
  }

  async function fetchAndSetCategories({
    refresh = false,
    setLoading = false,
  }) {
    if (setLoading) setIsLoading(true)
    try {
      const response = await categoryService.getAllCategoriesByCompanyId(
        {
          companyId: companyId,
          timezone: timezone,
          token: token,
        },
        refresh
      )

      let categories
      if (response && response.data && Number(response.statusCode) === 200) {
        categories = response.data.fileData
        setPermission(response.data.permission)
        setCategoriesData(categories)
      }

      const data = categories.map((cat) => ({
        dateCreated: convertToStandarDate(cat.dateCreated, language),
        dateModified: convertToStandarDate(cat.dateModified, language),
        expirationDate: '-',
        statusIA: t('fileManager.listView.header.status.ia.na'),
        filterPath: cat.filterPath,
        hasChild: cat.hasChild,
        id: cat.id,
        isFile: cat.isFile,
        name: cat.name,
        parentId: cat.parentId,
        size: cat.size === 0 ? '-' : cat.size,
        type: cat.type,
        imageUrl: cat.imageUrl,
        levelUser: cat.levelUser
      }))

      setFileData(data)
      return { categories, data }
    } catch (error) {
      console.log(error)
    } finally {
      if (setLoading) setIsLoading(false)
    }
  }

  async function getCategories() {
    if (firstLoad) setIsLoading(true)
    await fetchAndSetCategories({ refresh: false, setLoading: true })
    setFirstLoad(false)
  }

  async function getDocumentsBySubcategory(subcategoryPath, refresh) {
    try {
      const limit = 0
      const offset = 0
      const response = await categoryService.getDocumentsBySubcategory(
        subcategoryPath,
        companyId,
        limit,
        offset,
        token,
        refresh
      )

      let documents
      if (response && response.data && Number(response.statusCode) === 200) {
        documents = response.data
        setDocumentsData(documents)
      }

      const data = documents.map(doc => {
        let statusIA = doc.type === '.pdf' ? t('fileManager.listView.header.status.ia.no.analized') : t('fileManager.listView.header.status.ia.na')
        if (doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PENDING || doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PROCESSING) statusIA = t('fileManager.listView.header.status.ia.processing')
        if (doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_EXTRACTED) statusIA = t('fileManager.listView.header.status.ia.extracted')

        const data = {
          dateCreated: convertToStandarDate(doc.dateCreated, language),
          dateModified: convertToStandarDate(doc.dateModified, language),
          expirationDate: (doc.validationDate) ? convertToStandarDate(doc.validationDate, language) : '-',
          filterPath: doc.filterPath,
          hasChild: doc.hasChild,
          id: doc.id,
          isFile: doc.isFile,
          name: doc.name,
          parentId: doc.parentId,
          size: doc.size === 0 ? '-' : doc.size,
          type: doc.type,
          imageUrl: doc.imageUrl,
          statusIA
        }
        return data
      })

      // Create a map for faster lookups
      const fileDataMap = new Map(fileData.map((item) => [item.id, item]))

      // Update existing items or add new ones
      data.forEach((item) => {
        fileDataMap.set(item.id, item)
      })

      setFileData(Array.from(fileDataMap.values()))
    } catch (error) {
      console.log(error)
    }
  }

  async function createNewCategory({
    args,
    author = null,
    categoryName,
    companyId,
    createAllCompanies,
    defined = 'Custom',
    description = null,
    icon = null,
    key = null,
    level,
    parent = null,
    type,
  }) {
    toggleLoadingRequesting(true)
    try {
      const response = await categoryService.createNewCategory(
        categoryName,
        icon,
        key,
        description,
        defined,
        companyId,
        type,
        level,
        parent,
        createAllCompanies,
        token
      )

      if (response.data && response.statusCode === 200) {
        toggleLoadingRequesting(false)

        if (createAllCompanies) {
          toggleCompletedRequesting(
            t('fileManager.createCategory.dialog.completed.all'),
            true
          )
        } else {
          toggleCompletedRequesting(
            t('fileManager.createCategory.dialog.completed'),
            true
          )
        }
      } else {
        let errorMsg = t('fileManager.createCategory.dialog.error')

        switch (response.statusCode) {
          case 406:
            errorMsg = t('fileManager.createCategory.dialog.errorInputText')
            break
          case 410:
            errorMsg = t('fileManager.createCategory.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.createCategory.dialog.error.415')
            break
          case 420:
            errorMsg = t('fileManager.createCategory.dialog.error.420')
            break
          case 440:
            errorMsg = t('fileManager.createCategory.dialog.error.440')
            break
          case 450:
            errorMsg = t('fileManager.createCategory.dialog.error.450')
            break
          default:
            errorMsg = t('fileManager.createCategory.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.createCategory.dialog.error'))
      console.error('Error creating new category:', error)
    }
  }

  async function copyFiles({ companyId, files, target, timezone }) {
    toggleLoadingRequesting(true)
    try {
      const response = await categoryService.copyFiles({
        companyId,
        files,
        target,
        timezone,
        token,
      })

      if (response.data && response.statusCode === 200) {
        const { data } = response

        if (data.filesCopied.length >= 1 && data.filesNotCopied.length === 0) {
          toggleLoadingRequesting(false)
          toggleCompletedRequesting(
            t('fileManager.copyFile.dialog.success'),
            true
          )
        } else {
          toggleLoadingRequesting(false)
          setActionType('move')
          setShowFilesCopied(true)

          setFilesSuccess(data.filesCopied.length)
          setFfilesSuccessNames(data.filesCopied.map((file) => file.name))

          setFilesError(data.filesNotCopied.length)
          setFilesErrorNames(data.filesNotCopied.map((file) => file.name))
          refreshFileManager(true)
        }
      } else {
        let errorMsg = t('fileManager.copyFile.dialog.error')
        switch (response.statusCode) {
          case 405:
            errorMsg = t('fileManager.copyFile.dialog.error.405')
            break
          case 406:
            errorMsg = t('fileManager.copyFile.dialog.error.406')
            break
          case 407:
            errorMsg = t('fileManager.copyFile.dialog.error.407')
            break
          case 410:
            errorMsg = t('fileManager.copyFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.copyFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.copyFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.copyFile.dialog.error.417')
            break
          case 418:
            errorMsg = t('fileManager.copyFile.dialog.error.418')
            break
          case 419:
            errorMsg = t('fileManager.copyFile.dialog.error.419')
            break
          case 421:
            errorMsg = t('fileManager.copyFile.dialog.error.421')
            break
          default:
            errorMsg = t('fileManager.copyFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.copyFile.dialog.error'))
      console.error(error)
    }
  }

  async function moveFiles({ companyId, files, target }) {
    toggleLoadingRequesting(true)

    if (files[0].parentId === target.id) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.moveFile.dialog.error.sameOrigin'))
      return
    }

    try {
      const response = await categoryService.moveFiles({
        companyId,
        files,
        target,
        token,
      })

      if (response.data && response.statusCode === 200) {
        const { data } = response

        if (data.filesCopied.length >= 1 && data.filesNotCopied.length === 0) {
          toggleLoadingRequesting(false)
          toggleCompletedRequesting(
            t('fileManager.moveFile.dialog.success'),
            true
          )
        } else {
          toggleLoadingRequesting(false)
          setActionType('move')
          setShowFilesCopied(true)

          setFilesSuccess(data.filesCopied.length)
          setFfilesSuccessNames(data.filesCopied.map((file) => file.name))

          setFilesError(data.filesNotCopied.length)
          setFilesErrorNames(data.filesNotCopied.map((file) => file.name))
          refreshFileManager(true)
        }
      } else {
        let errorMsg = t('fileManager.copyFile.dialog.error')
        switch (response.statusCode) {
          case 405:
            errorMsg = t('fileManager.copyFile.dialog.error.405')
            break
          case 406:
            errorMsg = t('fileManager.copyFile.dialog.error.406')
            break
          case 407:
            errorMsg = t('fileManager.copyFile.dialog.error.407')
            break
          case 410:
            errorMsg = t('fileManager.copyFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.copyFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.copyFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.copyFile.dialog.error.417')
            break
          case 418:
            errorMsg = t('fileManager.copyFile.dialog.error.418')
            break
          case 419:
            errorMsg = t('fileManager.copyFile.dialog.error.419')
            break
          default:
            errorMsg = t('fileManager.copyFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.copyFile.dialog.error'))
      console.error(error)
    }
  }

  async function renameFile({ file, levelUser }) {
    toggleLoadingRequesting(true)

    try {
      const response = await categoryService.renameFile({
        file,
        companyId,
        levelUser,
        token,
      })

      if (response.statusCode === 200) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(
          t('fileManager.editFile.dialog.success'),
          true
        )
      } else {
        let errorMsg = t('fileManager.editFile.dialog.error')
        switch (response.statusCode) {
          case 403:
            errorMsg = t('fileManager.editFile.dialog.error.403')
            break
          case 410:
            errorMsg = t('fileManager.editFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.editFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.editFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.editFile.dialog.error.417')
            break
          case 420:
            errorMsg = t('fileManager.editFile.dialog.error.420')
            break
          case 430:
            errorMsg = t('fileManager.editFile.dialog.error.430')
            break
          case 435:
            errorMsg = t('fileManager.editFile.dialog.error.435')
            break
          default:
            errorMsg = t('fileManager.editFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.editFile.dialog.error'))
      console.error(error)
    }
  }

  async function deleteFiles({ companyId, files }) {
    toggleLoadingRequesting(true)

    try {
      const response = await categoryService.deleteFiles({
        companyId,
        files,
        token,
      })

      if (response.data && response.statusCode === 200) {
        const { data } = response

        if (
          data.filesDeleted.length >= 1 &&
          data.filesNotDeleted.length === 0
        ) {
          toggleLoadingRequesting(false)
          toggleCompletedRequesting(
            t('fileManager.deleteFile.dialog.success'),
            true,
            data.filesDeleted[0].id
          )
        } else {
          toggleLoadingRequesting(false)
          setActionType('delete')
          setShowFilesCopied(true)

          setFilesSuccess(data.filesDeleted.length)
          setFfilesSuccessNames(data.filesDeleted.map((file) => file.name))

          setFilesError(data.filesNotDeleted.length)
          setFilesErrorNames(data.filesNotDeleted.map((file) => file.name))
          refreshFileManager(true)
        }
      } else {
        let errorMsg = t('fileManager.deleteFile.dialog.error')
        switch (response.statusCode) {
          case 410:
            errorMsg = t('fileManager.copyFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.deleteFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.deleteFile.dialog.error.416')
            break
          case 418:
            errorMsg = t('fileManager.deleteFile.dialog.error.418')
            break
          case 420:
            errorMsg = t('fileManager.deleteFile.dialog.error.420')
            break
          case 430:
            errorMsg = t('fileManager.deleteFile.dialog.error.430')
            break
          case 431:
            errorMsg = t('fileManager.deleteFile.dialog.error.431')
            break
          case 432:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          case 440:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          default:
            errorMsg = t('fileManager.deleteFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.deleteFile.dialog.error'))
      console.error(error)
    }
  }

  async function downloadFile({ file, companyId }) {
    toggleLoadingRequesting(true)

    try {
      const response = await categoryService.downloadFile({
        file,
        companyId,
        token,
      })

      if (response.data && response.statusCode === 200) {
        toggleLoadingRequesting(false)
        window.location.assign(response.data)
        toggleCompletedRequesting(
          t('fileManager.downloadFile.dialog.success.browser'),
          false
        )
      } else if (
        (!response.data || response.data === '') &&
        response.statusCode === 200
      ) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(
          t('fileManager.downloadFile.dialog.success.email'),
          false
        )
      } else {
        let errorMsg = t('fileManager.downloadFile.dialog.error')
        switch (response.statusCode) {
          case 410:
            errorMsg = t('fileManager.renameFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.renameFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.renameFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.renameFile.dialog.error.417')
            break
          case 432:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          default:
            errorMsg = t('fileManager.downloadFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.downloadFile.dialog.error'))
      console.error(error)
    }
  }

  function addNewFile() {
    if (!fileObj.current) return

    const pathIds = fileObj.current.path.split('/')
    const categoryId = pathIds[1]
    const subPath = pathIds.slice(2, -1).join(',')

    const company = companies.filter((c) => c.company.id === companyId)[0]

    let newParams = new URLSearchParams()

    if (companyId) newParams.append('companyId', companyId)
    if (categoryId) newParams.append('categoryId', categoryId)
    if (subPath) newParams.append('path', subPath)

    history.push({
      pathname: `/digital-vault-upload`,
      search: newParams.toString(),
      state: {
        companyId,
        fileManagerPath: fileObj.current.path,
        selectedCategory: categoryId,
        selectedCompany: company,
        subcategoryPath: subPath
      },
    })
  }

  function depthLevelActions({
    filterPath,
    fileDetails,
    addFile = false,
    changeBtnStr = false,
    updateBreadcrumbs = false,
  }) {
    const depthLevel = checkDepth({ str: filterPath })

    if (addFile) {
      setShowAddFileBtn(depthLevel >= 3)
    }

    if (changeBtnStr) {
      setStrBtn(
        depthLevel >= 2
          ? t('fileManager.toolbar.newSubcategory')
          : t('fileManager.toolbar.newCategory')
      )
    }
  }

  function onMenuOpen(args) {
    const fileDetail = args.fileDetails[0]

    const setIconCss = (idSuffix, css) => {
      args.items.forEach((item) => {
        if (item.id === fileObj.current.element.id + idSuffix) {
          item.iconCss = css
        }
      })
    }

    const setMenuText = (idSuffix, text) => {
      args.items.forEach((item) => {
        if (item.id === fileObj.current.element.id + idSuffix) {
          item.text = text
        }
      })
    }

    setIconCss('_cm_cut', 'e-icons e-move')
    setMenuText('_cm_cut', t('fileManager.contextMenu.move'))

    setIconCss('_cm_analyze', 'e-icons e-analyze')

    if (!fileObj.current.activeRecords || !fileObj.current.selectedNodes) {
      setIconCss('_cm_paste', 'e-icons e-paste-disabled')
    } else if (
      !fileObj.current.activeRecords.length > 0 ||
      !fileObj.current.selectedNodes.length > 0
    ) {
      setIconCss('_cm_paste', 'e-icons e-fe-paste')
    }

    // Layout menu
    if (args.menuType === 'layout') {
      setMenuText('_cm_newfolder', strBtn)
    }

    // Several category folders selected
    if (
      args.menuType === 'folder' &&
      !fileDetail.parentId &&
      fileObj.current.selectedItems.length > 1
    ) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Cut',
        'Open',
        'Details'
      ])
      fileObj.current.enableMenuItems(['Copy'])
      setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      setIconCss('_cm_download', 'e-icons e-download-disabled')
      setIconCss('_cm_cut', 'e-icons e-move-disabled')
      setIconCss('_cm_open', 'e-icons e-open-disabled')
      setIconCss('_cm_details', 'e-icons e-details-disabled')
      setMenuText('_cm_details', t('fileManager.contextMenu.categoryDetails'))

      if (!permission.category.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.category.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.category.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.category.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.category.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Company main node
    if (
      args.menuType === 'folder' &&
      !fileDetail.filterPath &&
      !fileDetail.parentId
    ) {
      fileObj.current.disableMenuItems(['Download', 'Open'])
      setIconCss('_cm_open', 'e-icons e-open-disabled')
      setIconCss('_cm_copy', 'e-icons e-copy-disabled')
      setIconCss('_cm_paste', 'e-icons e-paste-disabled')
      setIconCss('_cm_cut', 'e-icons e-move-disabled')
      setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      setIconCss('_cm_download', 'e-icons e-download-disabled')
      setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      setMenuText('_cm_details', t('fileManager.contextMenu.companyDetails'))
      return
    }

    // One category folder selected
    if (
      (args.menuType === 'folder' &&
        !fileDetail.parentId &&
        fileObj.current.selectedItems.length === 0) ||
      (args.menuType === 'folder' &&
        !fileDetail.parentId &&
        fileObj.current.selectedItems.length === 1)
    ) {
      fileObj.current.disableMenuItems(['Cut'])
      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Copy',
        'Open',
        'Details'
      ])
      setIconCss('_cm_cut', 'e-icons e-move-disabled')
      setIconCss('_cm_open', 'e-icons e-fe-open')
      setIconCss('_cm_details', 'e-icons e-fe-details')
      setMenuText('_cm_details', t('fileManager.contextMenu.categoryDetails'))

      if (!permission.category.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.category.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.category.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.category.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.category.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Several subcategory folder selected
    if (
      args.menuType === 'folder' &&
      fileDetail.parentId !== 0 &&
      fileObj.current.selectedItems.length > 1
    ) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details'
      ])
      setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      setIconCss('_cm_download', 'e-icons e-download-disabled')
      setIconCss('_cm_open', 'e-icons e-open-disabled')
      setIconCss('_cm_details', 'e-icons e-details-disabled')
      setMenuText(
        '_cm_details',
        t('fileManager.contextMenu.subcategoryDetails')
      )

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // One subcategory folder selected
    if (
      (args.menuType === 'folder' &&
        fileDetail.parentId !== 0 &&
        fileObj.current.selectedItems.length === 0) ||
      (args.menuType === 'folder' &&
        fileDetail.parentId !== 0 &&
        fileObj.current.selectedItems.length === 1)
    ) {
      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Copy',
        'Cut',
        'Open',
        'Details'
      ])
      setIconCss('_cm_rename', 'e-icons e-fe-rename')
      setIconCss('_cm_download', 'e-icons e-fe-download')
      setIconCss('_cm_open', 'e-icons e-fe-open')
      setIconCss('_cm_details', 'e-icons e-fe-details')
      setMenuText(
        '_cm_details',
        t('fileManager.contextMenu.subcategoryDetails')
      )

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Several folder or files selected
    if (fileDetail.isFile && fileObj.current.selectedItems.length > 1) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details',
        'Analyze'
      ])
      setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      setIconCss('_cm_download', 'e-icons e-download-disabled')
      setIconCss('_cm_open', 'e-icons e-open-disabled')
      setIconCss('_cm_details', 'e-icons e-details-disabled')
      setMenuText('_cm_details', t('fileManager.contextMenu.fileDetails'))
      setIconCss('_cm_analyze', 'e-icons e-analyze-disabled')

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // One folder or file selected
    if (fileDetail.isFile && fileObj.current.selectedItems.length === 1) {
      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details',
        'Analyze'
      ])
      setIconCss('_cm_rename', 'e-icons e-fe-rename')
      setIconCss('_cm_download', 'e-icons e-fe-download')
      setIconCss('_cm_open', 'e-icons e-fe-open')
      setIconCss('_cm_details', 'e-icons e-fe-details')
      setIconCss('_cm_analyze', 'e-icons e-analyze')
      setMenuText('_cm_details', t('fileManager.contextMenu.fileDetails'))

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss('_cm_copy', 'e-icons e-copy-disabled')
        setIconCss('_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss('_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss('_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss('_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss('_cm_open', 'e-icons e-open-disabled')
      }

      if ((fileDetail.statusIA !== t('fileManager.listView.header.status.ia.no.analized'))) {
        fileObj.current.disableMenuItems(['Analyze'])
        setIconCss('_cm_analyze', 'e-icons e-analyze-disabled')
      }
      return
    }
  }

  function checkPermission({ action, type, str }) {
    const level = checkDepth({ str })

    const permissionMap = {
      category: {
        create: permission.category.create,
        copy: permission.category.copy,
        download: permission.category.download,
        delete: permission.category.delete,
        open: permission.category.open,
        rename: permission.category.rename,
      },
      subcategory: {
        create: permission.subcategory.create,
        copy: permission.subcategory.copy,
        download: permission.subcategory.download,
        delete: permission.subcategory.delete,
        open: permission.subcategory.open,
        rename: permission.subcategory.rename,
      }
    }

    let hasPermission = false
    let errorMsg = t('fileManager.permission')

    if (type === 'folder') {
      if (level === 1 && action === 'open') {
        hasPermission = true
      } else if (level === 2) {
        hasPermission = permissionMap.category[action]
        errorMsg = t(`fileManager.permission.category.${action}`)
      } else if (level >= 3) {
        hasPermission = permissionMap.subcategory[action]
        errorMsg = t(`fileManager.permission.subcategory.${action}`)
      }
    } else if (type === 'file') {
      hasPermission = permissionMap.subcategory[action]
      errorMsg = t(`fileManager.permission.file.${action}`)
    }

    if (!hasPermission && errorMsg) {
      toggleErrorRequesting(errorMsg)
    }

    return hasPermission
  }

  const onCreated = () => {
    const fileManagerElement = document.getElementById(
      'DigitalVault_filemanager'
    )
    const largeicon_view = document.getElementById(
      'DigitalVault_filemanager_layout'
    )
    const details_view = document.getElementById(
      'DigitalVault_filemanager_grid'
    )

    fileManagerElement.addEventListener('keyup', keyUpHandler, true)
    largeicon_view.addEventListener('keyup', keyUpHandler, true)
    details_view.addEventListener('keyup', keyUpHandler, true)

    try {
      if (fileObj && fileObj.current && fileObj.current.showToolbarItems) {
        fileObj.current.showToolbarItems(['Download'])
        fileObj.current.disableToolbarItems(['Download'])
      }
    } catch (e) {
    }
  }

  const keyUpHandler = (e) => {
    // Always use the latest selection info
    const currentPath = pathRef.current
    const currentIsFile = isFile.current

    // Map key codes to actions
    const keyActionMap = {
      67: 'copy',      // Ctrl+C
      86: 'copy',      // Ctrl+V
      88: 'copy',      // Ctrl+X 
      68: 'download',  // Ctrl+D
    }

    // Only handle if a mapped shortcut is pressed
    const action = keyActionMap[e.which]
    if ((e.ctrlKey || e.metaKey) && action) {
      // Determine type for permission check
      const type = currentIsFile ? 'file' : 'folder'

      if (!checkPermission({
        action,
        type,
        str: currentPath,
      })) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }
    }
  }

  async function onFileOpen(args) {
    const { fileDetails } = args
    if (
      !checkPermission({
        action: 'open',
        type: fileDetails.isFile ? 'file' : 'folder',
        str: fileDetails.filterPath
      })
    ) {
      args.cancel = true
      return
    }


    // Set breadcrumb items to show forward in the document detail
    if (fileDetails.isFile) {
      if (fileObj.current.pathNames.length > 1 && fileObj.current.path) {
        const pathNamesArray = fileObj.current.pathNames
        const pathIdsString = fileObj.current.path

        const pathIds = pathIdsString.split('/')
        pathIds.pop()

        const items = pathNamesArray.map((_, index) => ({
          id: pathIds[index],
          title: pathNamesArray[index],
        }))

        setBreadcrumbItems(items)
      }
    }

    // If it's a file selected, then we update the document id to change to the document view
    if (fileDetails.isFile && fileDetails.id) {
      setDocumentId(fileDetails.id)
      setSubcategoryId(fileDetails.parentId)
    }

    if (
      fileDetails &&
      fileDetails.id &&
      fileDetails.parentId &&
      !fileDetails.isFile
    ) {
      await getDocumentsBySubcategory(fileDetails.id, false)
    }

    // Update the toolbar buttons depending on the file manager depth
    if (!fileDetails.isFile) {
      depthLevelActions({
        filterPath: fileDetails.filterPath,
        addFile: true,
        changeBtnStr: true,
      })
    }
  }

  function onFileSelect(args) {
    const { fileDetails, selectedItems } = args

    if (!fileObj.current || !fileObj.current.selectedItems) {
      return
    }

    const multipleImmediate = fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
    if (multipleImmediate) {
      if (fileObj.current && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
        fileObj.current.toolbarModule.items.forEach((item) => {
          if (item.id === 'DigitalVault_filemanager_tb_analyzefile') item.visible = false
        })
      }
    }

    if (!fileDetails) {
      const remaining = (selectedItems && Array.isArray(selectedItems))
        ? selectedItems.length
        : (fileObj.current && fileObj.current.selectedItems
          ? fileObj.current.selectedItems.length
          : 0)
      if (remaining === 0) {
        if (fileObj.current && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
          fileObj.current.toolbarModule.items.forEach((item) => {
            if (item.id === 'DigitalVault_filemanager_tb_analyzefile') {
              item.visible = false
            }
          })
        }
        return
      }
    }
    if (!fileDetails) return
    pathRef.current = fileDetails.filterPath
    isFile.current = fileDetails.isFile
    if (fileObj.current) fileObj.current.documentSelected = null

    const multiSelection = fileObj.current && fileObj.current.selectedItems && fileObj.current.selectedItems.length > 1

    if (fileDetails.type === 'folder') {
      if (fileObj.current.selectedItems.length > 1) {
        fileObj.current.disableToolbarItems(['Download'])
      } else {
        fileObj.current.enableToolbarItems(['Download'])
      }

      // Category selected
      if (fileDetails.parentId === 0) {
        if (!permission.category.copy) {
          fileObj.current.disableToolbarItems(['Copy'])
        }
        if (!permission.category.download) {
          fileObj.current.disableToolbarItems(['Download'])
        }
        if (!permission.category.delete) {
          fileObj.current.disableToolbarItems(['Delete'])
        }
        if (!permission.category.rename) {
          fileObj.current.disableToolbarItems(['Rename'])
        }
      }

      // Subcategory selected
      if (fileDetails.parentId !== 0) {
        if (!permission.subcategory.copy) {
          fileObj.current.disableToolbarItems(['Copy'])
        }
        if (!permission.subcategory.download) {
          fileObj.current.disableToolbarItems(['Download'])
        }
        if (!permission.subcategory.delete) {
          fileObj.current.disableToolbarItems(['Delete'])
        }
        if (!permission.subcategory.rename) {
          fileObj.current.disableToolbarItems(['Rename'])
        }
      }
    }

    if (fileObj.current && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
      fileObj.current.toolbarModule.items.forEach((item) => {
        if (item.id === 'DigitalVault_filemanager_tb_analyzefile') {
          if (!fileDetails.isFile || multiSelection) {
            item.visible = false
          } else {
            const notAnalyzedText = t('fileManager.listView.header.status.ia.no.analized')
            const statusValue = fileDetails.statusIA || fileDetails.StatusAi || fileDetails.statusAi
            item.visible = statusValue === notAnalyzedText
          }
        }
      })
      setTimeout(() => {
        const multiPost = fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
        if (multiPost) {
          fileObj.current.toolbarModule.items.forEach((item) => {
            if (item.id === 'DigitalVault_filemanager_tb_analyzefile') item.visible = false
          })
        }
      }, 0)
    }

    if (fileDetails.isFile && args.action === 'select') {
      if (!permission.subcategory.copy) {
        fileObj.current.disableToolbarItems(['Copy'])
      }
      if (!permission.subcategory.download) {
        fileObj.current.disableToolbarItems(['Download'])
      }
      if (!permission.subcategory.delete) {
        fileObj.current.disableToolbarItems(['Delete'])
      }
      if (!permission.subcategory.rename) {
        fileObj.current.disableToolbarItems(['Rename'])
      }

      fileObj.current.documentSelected = fileDetails

    }
  }

  const sortComparer = (a, b) => {
    try {
      const dateA = parseCustomDate({
        dateStr: a,
        preferredLanguage: language,
      })
      const dateB = parseCustomDate({
        dateStr: b,
        preferredLanguage: language,
      })

      return dateA.getTime() - dateB.getTime()
    } catch (e) {
      return 0
    }
  }

  const popupCompanies = companies.map((item) => ({
    value: item.company.id,
    name: item.company.companyName,
  }))

  let resultData = [].slice.call(fileData)

  const userLevelOptions = [
    { name: t('digitalVaultCategoriesAdministration.add.options.noAccess'), levelUser: 'roleHibouDigitalVaultNotAccess' },
    { name: t('digitalVaultCategoriesAdministration.add.options.administrator'), levelUser: 'roleHibouDigitalVaultAdministrator' },
    { name: 'N1', levelUser: 'roleHibouDigitalVaultN1' },
    { name: 'N2', levelUser: 'roleHibouDigitalVaultN2' },
    { name: 'N3', levelUser: 'roleHibouDigitalVaultN3' },
    { name: 'N4', levelUser: 'roleHibouDigitalVaultN4' },
    { name: 'N5', levelUser: 'roleHibouDigitalVaultN5' }
  ]

  function CopyDialogDetails() {
    return (
      <DialogYesNoHLPure
        btnCancel={true}
        grid={true}
        cancel={() => history.push({ pathname: '/help-desk' })}
        handleCancel={() => {
          const url = `https://wa.me/${process.env.REACT_APP_PHONE_NUMBER}?text=${encodeURIComponent(t('whatsapp_default_text'))}`
          window.open(url, '_blank', 'noopener')
        }}
        handleSubmit={() => setShowFilesCopied(false)}
        saveBtn={'OK'}
        textBtnCancel={t('fileManager.copyFile.dialog.technicalSupport')}
        titleDialog={
          actionType === 'move'
            ? t('fileManager.copyFile.dialog.heading')
            : t('fileManager.deleteFile.dialog.heading.info')
        }
      >
        <ul>
          <li>
            {`${filesSuccess}`}{' '}
            {actionType === 'move'
              ? t('fileManager.copyFile.dialog.filesCopied')
              : t('fileManager.deleteFile.dialog.filesSuccess')}
            {filesSuccessNames.length > 0 && (
              <ul>
                <li>
                  <b>{filesSuccessNames.join(', ')}</b>
                </li>
              </ul>
            )}
          </li>
          <li>
            {`${filesError}`}{' '}
            {actionType === 'move'
              ? t('fileManager.copyFile.dialog.filesNotCopied')
              : t('fileManager.deleteFile.dialog.filesError')}
            {filesErrorNames.length > 0 && (
              <ul>
                <li>
                  <b>{filesErrorNames.join(', ')}</b>
                </li>
              </ul>
            )}
          </li>
        </ul>
        <p>{t('fileManager.copyFile.dialog')}</p>
      </DialogYesNoHLPure>
    )
  }

  async function handleShowModalEntities() {
    const documentId = (fileObj && fileObj.current && fileObj.current.documentSelected) ? fileObj.current.documentSelected.id : null
    if (!documentId) {
      toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
      return
    }
    setDocumentSelectedId(documentId)
    try {
      toggleLoadingRequesting(true)
      const document = await digitalVaultDocumentService.getDocumentPages(documentId, token)
      if (document && document.data && Number(document.statusCode) === 200) {
        const docsCreditsOCR = setCreditsToDocuments(document.data.files, t)
        if (docsCreditsOCR && docsCreditsOCR.documentsToOCR && docsCreditsOCR.documentsToOCR.length > 0) {
          let docsCreditsOCR = setCreditsToDocuments(document.data.files, t)
          let docsToOCR = (docsCreditsOCR && docsCreditsOCR.documentsToOCR) ? docsCreditsOCR.documentsToOCR : []
          let totalCredits = (docsCreditsOCR && docsCreditsOCR.totalCredits) ? docsCreditsOCR.totalCredits : 0
          setDocumentsToOCR(docsToOCR)
          setTotalCredits(totalCredits)
          setShowModalEntities(true)
          toggleLoadingRequesting(false)
        } else {
          toggleLoadingRequesting(false)
          toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
        }

      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
      }

    } catch (error) {
      toggleLoadingRequesting(true)
      toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
    }
  }

  async function saveExtractedEntities() {
    let messageError = t('digitalVaultDocumentAction.errors.save.entities')
    setShowModalEntities(false)
    const documentId = documentSelectedId
    if (!documentId) {
      toggleErrorRequesting(messageError)
      return
    }

    try {
      let documentsSendToOCR = documentsToOCR.filter(doc => doc.value)
      documentsSendToOCR = documentsSendToOCR.map((doc) => {
        doc.fileId = doc.id
        return doc
      })

      if (documentsSendToOCR.length === 0) {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
        return
      }

      toggleLoadingRequesting(true)
      closeModalEntities()
      const extractEntities = await digitalVaultDocumentService.sendEntitiesToExtract(documentId, documentsSendToOCR, token)
      if (extractEntities && extractEntities.data && Number(extractEntities.statusCode) === 200) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting('', true)
      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(messageError)
      }
    } catch (error) {
      console.error(error)
      toggleLoadingRequesting(false)
      toggleErrorRequesting(messageError)

    }

  }

  function closeModalEntities() {
    setDocumentsToOCR([])
    setTotalCredits(0)
    setShowModalEntities(false)
    fileObj.current.documentSelected = null
    setDocumentSelectedId(null)
  }

  function onClickCheckBox(e, dataItem) {
    let copyDocs = [...documentsToOCR]
    let valueCheck = e.target.checked
    let totalCredits = 0
    copyDocs = copyDocs.map((doc) => {
      if (doc.id === dataItem.id) {
        doc.value = valueCheck
      }

      if (doc.value) {
        totalCredits = totalCredits + doc.credits
      }
      return doc
    })

    setDocumentsToOCR(copyDocs)
    setTotalCredits(totalCredits)
  }

  useEffect(() => {
    if (
      selectedCompany &&
      selectedCompany.company &&
      selectedCompany.company.id
    ) {
      setCompanyId(selectedCompany.company.id)
    }
  }, [selectedCompany])

  useEffect(() => {
    if (companyId && timezone) {
      getCategories()
    }
  }, [companyId, timezone])

  useEffect(() => {
    return () => {
      setFileManagerPath('/')
    }
  }, [companyId])

  useEffect(() => {
    if (pendingSubcategoryId) {
      getDocumentsBySubcategory(pendingSubcategoryId, true)
      setPendingSubcategoryId(null)
    }
  }, [fileData, pendingSubcategoryId])

  useEffect(() => {
    if (state && state.subcategoryPath && !firstLoad) {
      getDocumentsBySubcategory(state.subcategoryPath, true)
    }

    if (subcategoryId && !firstLoad) {
      getDocumentsBySubcategory(subcategoryId, true)
    }
  }, [subcategoryId, state, firstLoad])

  useEffect(() => {
    async function loadL10n() {
      const response = await fetch(
        '/assets/syncfusion/translations/file-manager.json'
      )
      const locales = await response.json()
      if (locales[language]) {
        L10n.load({
          [language]: locales[language],
        })
      }
    }
    loadL10n()
  }, [language])

  if (isLoading && firstLoad) return <SimpleLoading />

  return (
    <>
      <DialogSystemGLPure
        showLoadingRequesting={showLoadingRequesting}
        toggleLoadingRequesting={toggleLoadingRequesting}
        showCompletedRequesting={showCompletedRequesting}
        toggleCompletedRequesting={toggleCompletedRequesting}
        showErrorRequesting={showErrorRequesting}
        toggleErrorRequesting={toggleErrorRequesting}
        message={messageDialog}
        messageComplete={messageDialog}
      />
      {showFilesCopied && <CopyDialogDetails />}
      <SyncfusionFileManager
        addNewFile={addNewFile}
        categoriesData={categoriesData}
        checkDepth={checkDepth}
        checkPermission={checkPermission}
        companies={popupCompanies}
        companyId={companyId}
        copyFiles={copyFiles}
        createNewCategory={createNewCategory}
        deleteFiles={deleteFiles}
        documentsData={documentsData}
        downloadFile={downloadFile}
        fileManagerPath={fileManagerPath}
        fileObj={fileObj}
        fileSystemData={resultData}
        isLoading={isLoading}
        language={language}
        moveFiles={moveFiles}
        onCreated={onCreated}
        onFileOpen={onFileOpen}
        onFileSelect={onFileSelect}
        onMenuOpen={onMenuOpen}
        permission={permission}
        renameFile={renameFile}
        refreshFileManager={refreshFileManager}
        setCompanyId={setCompanyId}
        setIsLoading={setIsLoading}
        setFirstLoad={setFirstLoad}
        showAddFileBtn={showAddFileBtn}
        sortComparer={sortComparer}
        strBtn={strBtn}
        t={t}
        userLevelOptions={userLevelOptions}
        toggleErrorRequesting={toggleErrorRequesting}
        showAnalyzeBtn={showAnalyzeBtn}
        showModalEntities={showModalEntities}
        handleShowModalEntities={handleShowModalEntities}
        closeModalEntities={closeModalEntities}
        saveExtractedEntities={saveExtractedEntities}
        documentsToOCR={documentsToOCR}
        totalCredits={totalCredits}
        onClickCheckBox={onClickCheckBox}
      />
    </>
  )
}

export default SyncfusionFileManagerAction
